import { useState, useMemo, useCallback } from 'react';
import {
  Card,
  Typography,
  Button,
  Row,
  Col,
  theme,
  Flex,
  Empty,
  Table,
  Tag,
  Skeleton,
  message,
} from 'antd';
import {
  BookOutlined,
  SyncOutlined,
  DownOutlined,
  UpOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { createModuleRouteGuard } from '@/modules/routeGuards';
import PageTransition from '@/components/ui/PageTransition';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { StatCardGrid } from '@/components/ui/StatCardGrid';
import { DateTimeCell } from '@/components/shared/DateTimeCell';
import { useStatisticsQuery, useCompanyQuery, useIndexMutation } from '../api';
import type { StatisticsEntry } from '../api';
import { CompanyDetails } from '../components/CompanyDetails';
import { OpeningHoursCard } from '../components/OpeningHoursCard';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text, Link } = Typography;
const { useToken } = theme;

// ---------------------------------------------------------------------------
// Route definition
// ---------------------------------------------------------------------------

export const Route = createFileRoute('/manage/modules/knowledgeBase/')({
  component: KnowledgeBaseModulePage,
  beforeLoad: createModuleRouteGuard('knowledge-base-module'),
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatJobId = (entry: StatisticsEntry): string => {
  const date = dayjs(entry.queuedAt);
  return `IDX-${date.format('YYMM')}-${String(entry.id).padStart(3, '0')}`;
};

const extractBaseUrl = (pages: string[]): string => {
  if (!pages.length) return '';
  try {
    const url = new URL(pages[0]);
    return `${url.protocol}//${url.hostname}`;
  } catch {
    return '';
  }
};

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

/**
 * Knowledge Base Module page.
 *
 * Layout:
 * 1. Header — title, subtitle, "Re-index Website" action.
 * 2. Statistics row — Total Indexings, Total Pages, Last Sync, System Health.
 * 3. Indexing Jobs — searchable table with expandable row details.
 * 4. Company Information — address/contact details + opening hours.
 */
export function KnowledgeBaseModulePage() {
  const { token } = useToken();
  const { t } = useTranslation('knowledge-base-module');
  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly React.Key[]>([]);
  const [messageApi, contextHolder] = message.useMessage();

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const { data: statisticsData, isLoading: statsLoading, refetch } = useStatisticsQuery();
  const { data: companyData, isLoading: companyLoading } = useCompanyQuery();
  const indexMutation = useIndexMutation();

  const company = companyData?.data?.[0] ?? null;

  // -------------------------------------------------------------------------
  // Derived values
  // -------------------------------------------------------------------------

  const stats = useMemo(() => {
    const entries = statisticsData?.data ?? [];
    const totalIndexings = statisticsData?.total ?? entries.length;
    const totalPages = entries.reduce((sum, e) => sum + e.pagesCount, 0);
    const lastEntry = entries.length > 0 ? entries[0] : null;
    const lastSync = lastEntry ? dayjs(lastEntry.queuedAt) : null;
    const isSyncToday = lastSync?.isSame(dayjs(), 'day');
    const allAcknowledged = entries.length > 0 && entries.every((e) => e.acknowledged);

    return {
      totalIndexings,
      totalPages,
      lastSyncText: lastSync ? (isSyncToday ? t('stats.today') : lastSync.fromNow()) : t('stats.never'),
      isHealthy: allAcknowledged,
      baseUrl: lastEntry ? extractBaseUrl(lastEntry.pages) : '',
    };
  }, [statisticsData, t]);

  /** Configuration for the four dashboard statistic cards. */
  const statCards = useMemo(
    () => [
      {
        title: t('stats.totalIndexings'),
        value: stats.totalIndexings,
        icon: <DatabaseOutlined />,
        color: token.colorPrimary,
        subtitle: stats.baseUrl
          ? stats.baseUrl.replace(/^https?:\/\//, '')
          : t('stats.noDataYet'),
      },
      {
        title: t('stats.totalPages'),
        value: stats.totalPages,
        icon: <FileTextOutlined />,
        color: token.colorSuccess,
        subtitle: t('stats.acrossRuns', { count: stats.totalIndexings }),
      },
      {
        title: t('stats.lastSync'),
        value: stats.lastSyncText,
        icon: <ClockCircleOutlined />,
        color: token.colorWarning,
        subtitle: stats.lastSyncText === t('stats.never')
          ? t('stats.noIndexingRuns')
          : stats.lastSyncText === t('stats.today')
            ? dayjs(statisticsData?.data?.[0]?.queuedAt).format('HH:mm')
            : dayjs(statisticsData?.data?.[0]?.queuedAt).format('MMM DD, YYYY HH:mm'),
      },
      {
        title: t('stats.systemHealth'),
        value: stats.isHealthy ? t('stats.stable') : t('stats.unstable'),
        icon: <CheckCircleOutlined />,
        color: stats.isHealthy ? token.colorSuccess : token.colorWarning,
        subtitle: stats.isHealthy
          ? t('stats.allAcknowledged')
          : t('stats.requiresAttention'),
      },
    ],
    [stats, statisticsData, token, t],
  );

  const entries = useMemo(() => statisticsData?.data ?? [], [statisticsData]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleReindex = useCallback(async () => {
    try {
      await indexMutation.mutateAsync();
      messageApi.success(t('reindexSuccess'));
      refetch();
    } catch {
      messageApi.error(t('reindexError'));
    }
  }, [indexMutation, messageApi, refetch, t]);

  // -------------------------------------------------------------------------
  // Table configuration
  // -------------------------------------------------------------------------

  const columns = useMemo(
    () => [
      {
        title: t('table.jobId'),
        key: 'jobId',
        render: (_: unknown, record: StatisticsEntry) => (
          <Text strong style={{ fontFamily: token.fontFamilyCode, fontSize: token.fontSizeSM, whiteSpace: 'nowrap' }}>
            {formatJobId(record)}
          </Text>
        ),
      },
      {
        title: t('table.status'),
        key: 'status',
        render: (_: unknown, record: StatisticsEntry) => (
          <Tag color={record.acknowledged ? 'blue' : 'orange'} variant="filled">
            {record.acknowledged ? t('table.acknowledged') : t('table.pending')}
          </Tag>
        ),
      },
      {
        title: t('table.queuedAt'),
        dataIndex: 'queuedAt',
        key: 'queuedAt',
        render: (val: string) => <DateTimeCell value={val} />,
      },
      {
        title: t('table.createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (val: string) => <DateTimeCell value={val} />,
      },
      {
        title: t('table.pagesCount'),
        key: 'pagesCount',
        render: (_: unknown, record: StatisticsEntry) => (
          <Text strong>{t('table.pages', { count: record.pagesCount })}</Text>
        ),
      },
      {
        title: t('table.actions'),
        key: 'actions',
        align: 'right' as const,
        render: (_: unknown, record: StatisticsEntry) => {
          const isExpanded = expandedRowKeys.includes(record.id);
          return (
            <Link
              onClick={(e) => {
                e.stopPropagation();
                setExpandedRowKeys((prev) =>
                  prev.includes(record.id)
                    ? prev.filter((k) => k !== record.id)
                    : [...prev, record.id],
                );
              }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>
                {isExpanded ? t('table.hideDetails') : t('table.viewDetails')}{' '}
                {isExpanded ? (
                  <UpOutlined style={{ fontSize: token.fontSizeSM }} />
                ) : (
                  <DownOutlined style={{ fontSize: token.fontSizeSM }} />
                )}
              </span>
            </Link>
          );
        },
      },
    ],
    [token, expandedRowKeys, t],
  );

  const expandedRowRender = useCallback(
    (record: StatisticsEntry) => {
      const baseUrl = extractBaseUrl(record.pages);
      return (
        <div
          style={{
            padding: token.paddingMD,
            background: token.colorFillQuaternary,
            borderRadius: token.borderRadiusLG,
            margin: `0 ${token.marginSM}px ${token.marginSM}px`,
          }}
        >
          <Flex
            justify="space-between"
            align="center"
            wrap="wrap"
            gap={token.marginXS}
            style={{ marginBottom: token.marginSM }}
          >
            <Text strong ellipsis={{ tooltip: true }} style={{ minWidth: 0 }}>
              <GlobalOutlined style={{ marginInlineEnd: token.marginXS, color: token.colorPrimary }} />
              {baseUrl.replace(/^https?:\/\//, '')}
            </Text>
            <Tag color="blue" variant="filled">
              {t('table.pagesIndexed', { count: record.pagesCount })}
            </Tag>
          </Flex>
          <Row gutter={[token.marginXS, token.marginXS]}>
            {record.pages.map((page, idx) => {
              let pathname = '/';
              try {
                pathname = new URL(page).pathname || '/';
              } catch {
                /* ignore */
              }
              return (
                <Col xs={24} sm={12} lg={8} key={idx}>
                  <a
                    href={page}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={page}
                    style={{
                      display: 'block',
                      padding: `${token.paddingXXS}px ${token.paddingXS}px`,
                      background: token.colorBgContainer,
                      borderRadius: token.borderRadiusSM,
                      border: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,
                      fontSize: token.fontSizeSM,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {pathname}
                  </a>
                </Col>
              );
            })}
          </Row>
        </div>
      );
    },
    [token],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <PageTransition animation="fadeInUp">
      {contextHolder}

      <PageHeader
        icon={<BookOutlined />}
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={
          <Button
            type="primary"
            icon={<SyncOutlined spin={indexMutation.isPending} />}
            loading={indexMutation.isPending}
            onClick={handleReindex}
          >
            {t('reindexWebsite')}
          </Button>
        }
      />

      {/* Statistics cards */}
      <StatCardGrid>
        {statCards.map((card) => (
          <StatCard
            key={card.title}
            label={card.title}
            value={card.value}
            hint={card.subtitle}
            icon={card.icon}
            color={card.color}
            loading={statsLoading}
          />
        ))}
      </StatCardGrid>

      {/* Indexing Jobs table */}
      <Card
        title={t('table.title')}
        style={{
          overflow: 'hidden',
          marginBottom: token.marginLG,
        }}
        styles={{ body: { padding: 0 } }}
      >
        <Table<StatisticsEntry>
          dataSource={entries}
          columns={columns}
          rowKey="id"
          loading={statsLoading}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('table.empty')} /> }}
          expandable={{
            expandedRowRender,
            expandedRowKeys: expandedRowKeys as React.Key[],
            onExpandedRowsChange: setExpandedRowKeys,
            expandIcon: () => null,
          }}
          pagination={{
            pageSize: 10,
            size: 'small',
            showTotal: (total, range) =>
              t('table.paginationTotal', { start: range[0], end: range[1], total }),
          }}
        />
      </Card>

      {/* Company Information */}
      {companyLoading ? (
        <Row gutter={[token.marginMD, token.marginMD]}>
          <Col xs={24} lg={12}>
            <Card>
              <Skeleton active paragraph={{ rows: 5 }} />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card>
              <Skeleton active paragraph={{ rows: 7 }} />
            </Card>
          </Col>
        </Row>
      ) : company ? (
        <Row gutter={[token.marginMD, token.marginMD]}>
          <Col xs={24} lg={12}>
            <CompanyDetails company={company} />
          </Col>
          {company.openinghours && (
            <Col xs={24} lg={12}>
              <OpeningHoursCard openinghours={company.openinghours} />
            </Col>
          )}
        </Row>
      ) : null}
    </PageTransition>
  );
}
